import apiClient from './apiClient';
import { handleApiError, formatApiResponse } from './api';
import procesosServiceModule from './procesosService';
import formulacionesServiceModule from './formulacionesService';
import inventarioServiceModule from './inventarioService';
import dashboardServiceModule from './dashboardService';

// Servicio para autenticación
export const authService = {
  // Iniciar sesión
  login: async (credentials) => {
    try {
      const response = await apiClient.post('/auth/login', credentials);
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  // Cerrar sesión
  logout: async () => {
    try {
      const response = await apiClient.post('/auth/logout');
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  // Obtener perfil del usuario
  getProfile: async () => {
    try {
      const response = await apiClient.get('/auth/profile');
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  // Actualizar perfil del usuario
  updateProfile: async (data) => {
    try {
      const response = await apiClient.put('/auth/profile', data);
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }
};

// Servicio para órdenes de compra
export const comprasService = {
  // Obtener lista de órdenes de compra
  getOrdenes: async (params = {}) => {
    try {
      const response = await apiClient.get('/purchase-orders', { params });
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  // Obtener orden de compra por ID
  getOrdenById: async (id) => {
    try {
      const response = await apiClient.get(`/purchase-orders/${id}`);
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  // Crear nueva orden de compra
  crearOrden: async (data) => {
    try {
      console.log('Creando orden de compra con datos:', data);
      const response = await apiClient.post('/purchase-orders', data);
      return formatApiResponse(response);
    } catch (error) {
      console.error('Error en crearOrden:', error);
      return handleApiError(error);
    }
  },

  // Actualizar orden de compra
  actualizarOrden: async (id, data) => {
    try {
      console.log('Actualizando orden de compra con datos:', data);
      const response = await apiClient.put(`/purchase-orders/${id}`, data);
      return formatApiResponse(response);
    } catch (error) {
      console.error('Error en actualizarOrden:', error);
      return handleApiError(error);
    }
  },

  // Enviar orden de compra por email
  enviarOrden: async (id, emailData = {}) => {
    try {
      const defaultEmailData = {
        email_addresses: [],
        subject: 'Nueva Orden de Compra',
        message: 'Adjuntamos nueva orden de compra para su revisión.',
        send_copy_to_self: true,
        ...emailData
      };

      const response = await apiClient.post(`/purchase-orders/${id}/send`, defaultEmailData);
      return formatApiResponse(response);
    } catch (error) {
      console.error('Error enviando orden de compra:', error);
      return handleApiError(error);
    }
  },

  // Aprobar/Confirmar orden de compra
  aprobarOrden: async (id, approvalData = {}) => {
    try {
      const defaultApprovalData = {
        notes: 'Orden aprobada',
        ...approvalData
      };

      const response = await apiClient.post(`/purchase-orders/${id}/confirm`, defaultApprovalData);
      return formatApiResponse(response);
    } catch (error) {
      console.error('Error aprobando orden de compra:', error);
      return handleApiError(error);
    }
  },

  // Recibir productos de orden de compra
  recibirOrden: async (id, receiptData) => {
    try {
      const response = await apiClient.post(`/purchase-orders/${id}/receive`, receiptData);
      return formatApiResponse(response);
    } catch (error) {
      console.error('Error recibiendo orden de compra:', error);
      return handleApiError(error);
    }
  },

  // Cancelar orden de compra
  cancelarOrden: async (id) => {
    try {
      const response = await apiClient.delete(`/purchase-orders/${id}`);
      return formatApiResponse(response);
    } catch (error) {
      console.error('Error cancelando orden de compra:', error);
      return handleApiError(error);
    }
  },

  // Obtener recepciones de una orden de compra
  getRecepciones: async (id) => {
    try {
      const response = await apiClient.get(`/purchase-orders/${id}/receipts`);
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  // Obtener estadísticas de órdenes de compra
  getEstadisticas: async () => {
    try {
      const response = await apiClient.get('/purchase-orders/stats/overview');
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  // Duplicar orden de compra
  duplicarOrden: async (id, newData = {}) => {
    try {
      console.log('Duplicando orden de compra:', id, newData);
      const response = await apiClient.post(`/purchase-orders/${id}/duplicate`, newData);
      return formatApiResponse(response);
    } catch (error) {
      console.error('Error duplicando orden de compra:', error);
      return handleApiError(error);
    }
  },

  // Generar PDF de orden de compra
  generarPDF: async (id) => {
    try {
      const response = await apiClient.get(`/purchase-orders/${id}/pdf`, {
        responseType: 'blob'
      });
      return {
        success: true,
        data: response.data,
        headers: response.headers
      };
    } catch (error) {
      return handleApiError(error);
    }
  },

  // Obtener productos con stock bajo para órdenes automáticas
  getProductosStockBajo: async () => {
    try {
      const response = await apiClient.get('/products/low-stock');
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  // Obtener historial de órdenes por proveedor
  getHistorialProveedor: async (supplierId, params = {}) => {
    try {
      const response = await apiClient.get(`/purchase-orders/supplier/${supplierId}`, { params });
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }
};

// Servicio para gestión de proveedores y compras
export const gestionComprasService = {
  // Obtener resumen de órdenes pendientes
  getOrdenesPendientes: async () => {
    try {
      const response = await apiClient.get('/v1/purchase-orders', {
        params: { status: 'PENDING', limit: 50 }
      });
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  // Obtener órdenes que requieren atención
  getOrdenesRequierenAtencion: async () => {
    try {
      const response = await apiClient.get('/v1/purchase-orders/urgent');
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  // Crear orden automática basada en stock bajo
  crearOrdenAutomatica: async (supplierId, productos) => {
    try {
      const orderData = {
        supplier_id: supplierId,
        priority: 'HIGH',
        notes: 'Orden automática generada por stock bajo',
        items: productos.map(p => ({
          product_id: p.id,
          quantity: p.suggested_quantity || p.min_stock * 2,
          unit_cost: p.cost_price || 0,
          description: p.name || p.nombreProducto
        }))
      };
      
      const response = await apiClient.post('/v1/purchase-orders/auto-create', orderData);
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }
};

export const clientesService = {
  // Obtener lista de clientes
  getClientes: async (params = {}) => {
    try {
      const response = await apiClient.get('/clients', { params });
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  // Obtener cliente por ID
  getCliente: async (id) => {
    try {
      const response = await apiClient.get(`/clients/${id}`);
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  // Crear nuevo cliente
  createCliente: async (data) => {
    try {
      const response = await apiClient.post('/clients', data);
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  // Actualizar cliente
  updateCliente: async (id, data) => {
    try {
      const response = await apiClient.put(`/clients/${id}`, data);
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  // Eliminar cliente
  deleteCliente: async (id) => {
    try {
      const response = await apiClient.delete(`/clients/${id}`);
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  // Obtener historial de compras del cliente
  getHistorialCompras: async (id, params = {}) => {
    try {
      const response = await apiClient.get(`/clients/${id}/historial`, { params });
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  // Obtener facturas del cliente
  getFacturasCliente: async (id, params = {}) => {
    try {
      const response = await apiClient.get(`/clients/${id}/invoices`, { params });
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  // Obtener pagos del cliente
  getPagosCliente: async (id, params = {}) => {
    try {
      const response = await apiClient.get(`/clients/${id}/payments`, { params });
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  // Obtener historial de compras del cliente
  getHistorialCompras: async (id, params = {}) => {
    try {
      const response = await apiClient.get(`/clients/${id}/purchase-history`, { params });
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  // Obtener estado de cuenta del cliente
  getEstadoCuenta: async (id, params = {}) => {
    try {
      const response = await apiClient.get(`/clients/${id}/account-statement`, { params });
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  // Obtener estado de créditos del cliente
  getCreditStatus: async (id, params = {}) => {
    try {
      const response = await apiClient.get(`/clients/${id}/credit-status`, { params });
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }
,

  // ========================================================================
  // MÉTODOS PARA NOTAS DE CLIENTES
  // ========================================================================

  // Obtener notas de un cliente
  getNotas: async (id, params = {}) => {
    try {
      const response = await apiClient.get(`/clients/${id}/notes`, { params });
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  // Crear nueva nota para un cliente
  createNota: async (id, data) => {
      console.log("📤 POST /clients/" + id + "/notes con data:", data);
    try {
      const response = await apiClient.post(`/clients/${id}/notes`, data);
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  // Actualizar nota de un cliente
  updateNota: async (clientId, noteId, data) => {
    try {
      const response = await apiClient.put(`/clients/${clientId}/notes/${noteId}`, data);
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  // Eliminar nota de un cliente
  deleteNota: async (clientId, noteId) => {
    try {
      const response = await apiClient.delete(`/clients/${clientId}/notes/${noteId}`);
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  // ========================================================================
  // MÉTODOS PARA EMAILS DE CLIENTES
  // ========================================================================

  // Obtener historial de emails enviados a un cliente
  getEmails: async (id, params = {}) => {
    try {
      const response = await apiClient.get(`/clients/${id}/emails`, { params });
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  // Enviar email a un cliente
  enviarEmail: async (id, data) => {
    try {
      const response = await apiClient.post(`/clients/${id}/emails/send`, data);
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }
};

// Servicio para productos
export const productosService = {
  // Obtener lista de productos
  getProductos: async (params = {}) => {
    try {
      const response = await apiClient.get('/products', { params });
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  // Obtener producto por ID
  getProducto: async (id) => {
    try {
      const response = await apiClient.get(`/products/${id}`);
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  // Obtener formulación de un producto
  getProductoFormulation: async (id) => {
    try {
      const response = await apiClient.get(`/products/${id}/formulation`);
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  // Actualizar formulación de un producto
  updateProductoFormulation: async (id, formulations) => {
    try {
      const response = await apiClient.put(`/products/${id}/formulation`, formulations);
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  // Crear nuevo producto
  createProducto: async (data) => {
    try {
      console.log('Creando producto con datos:', data);
      const response = await apiClient.post('/products', data);
      return formatApiResponse(response);
    } catch (error) {
      console.error('Error en createProducto:', error);
      return handleApiError(error);
    }
  },

  // Actualizar producto
  updateProducto: async (id, data) => {
    try {
      console.log('Actualizando producto con datos:', data);
      const response = await apiClient.put(`/products/${id}`, data);
      return formatApiResponse(response);
    } catch (error) {
      console.error('Error en updateProducto:', error);
      return handleApiError(error);
    }
  },

  // Eliminar producto (hard delete - usar solo si no tiene ventas)
  deleteProducto: async (id) => {
    try {
      const response = await apiClient.delete(`/products/${id}`);
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  // Soft delete - Cambiar status a DISCONTINUED o INACTIVE
  softDeleteProducto: async (id, hasTransactions = false) => {
    try {
      // Si tiene transacciones, marcar como DISCONTINUED para mantener historial
      // Si no tiene transacciones, marcar como INACTIVE
      const newStatus = hasTransactions ? 'DISCONTINUED' : 'INACTIVE';
      const response = await apiClient.put(`/products/${id}`, { status: newStatus });
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  // Verificar si producto tiene transacciones (ventas, facturas, movimientos)
  checkProductTransactions: async (id) => {
    try {
      const response = await apiClient.get(`/products/${id}/has-transactions`);
      return formatApiResponse(response);
    } catch (error) {
      // Si el endpoint no existe, asumir que tiene transacciones por seguridad
      return { success: true, data: { has_transactions: true } };
    }
  },

  // Subir imagen de producto
  uploadImage: async (id, file) => {
    try {
      const formData = new FormData();
      formData.append('image', file);
      
      const response = await apiClient.post(`/products/${id}/upload-image`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  // Obtener inventario/stock de productos
  getStock: async (params = {}) => {
    try {
      const response = await apiClient.get('/products/stock', { params });
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  // Actualizar stock de producto
  updateStock: async (id, stockData) => {
    try {
      const response = await apiClient.post(`/products/${id}/stock`, stockData);
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  // Obtener historial de ventas del producto
  getHistorialVentas: async (id, params = {}) => {
    try {
      const response = await apiClient.get(`/products/${id}/sales-history`, { params });
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  // Validar código de producto
  validateCode: async (code, excludeId = null) => {
    try {
      const params = { code };
      if (excludeId) params.exclude_id = excludeId;
      
      const response = await apiClient.get('/v1/products/validate-code', { params });
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  // Validar código de barras
  validateBarcode: async (barcode, excludeId = null) => {
    try {
      const params = { barcode };
      if (excludeId) params.exclude_id = excludeId;

      const response = await apiClient.get('/v1/products/validate-barcode', { params });
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  // Obtener formulación de un producto
  getProductFormulation: async (id) => {
    try {
      const response = await apiClient.get(`/products/${id}/formulation`);
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  // Crear formulación para un producto
  createFormulation: async (productId, formulationData) => {
    try {
      console.log('Creando formulación para producto:', productId, formulationData);
      const response = await apiClient.post(`/products/${productId}/formulation`, formulationData);
      return formatApiResponse(response);
    } catch (error) {
      console.error('Error en createFormulation:', error);
      return handleApiError(error);
    }
  },

  // Actualizar formulación de un producto
  updateFormulation: async (productId, formulationId, formulationData) => {
    try {
      console.log('Actualizando formulación:', formulationId, 'del producto:', productId);
      const response = await apiClient.put(`/products/${productId}/formulation/${formulationId}`, formulationData);
      return formatApiResponse(response);
    } catch (error) {
      console.error('Error en updateFormulation:', error);
      return handleApiError(error);
    }
  },

  // Eliminar ingrediente de formulación
  deleteFormulationIngredient: async (productId, formulationId) => {
    try {
      const response = await apiClient.delete(`/products/${productId}/formulation/${formulationId}`);
      return formatApiResponse(response);
    } catch (error) {
      console.error('Error en deleteFormulationIngredient:', error);
      return handleApiError(error);
    }
  },

  // ===== GESTIÓN DE IMÁGENES MÚLTIPLES =====

  // Subir imagen de producto (nuevo sistema multi-imagen)
  uploadProductImage: async (productId, file, metadata = {}) => {
    try {
      const formData = new FormData();
      formData.append('file', file);

      // Agregar metadatos opcionales
      if (metadata.alt_text) formData.append('alt_text', metadata.alt_text);
      if (metadata.is_primary !== undefined) formData.append('is_primary', metadata.is_primary);
      if (metadata.display_order !== undefined) formData.append('display_order', metadata.display_order);

      const response = await apiClient.post(`/products/${productId}/images`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      return formatApiResponse(response);
    } catch (error) {
      console.error('Error en uploadProductImage:', error);
      return handleApiError(error);
    }
  },

  // Obtener todas las imágenes de un producto
  getProductImages: async (productId) => {
    try {
      const response = await apiClient.get(`/products/${productId}/images`);
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  // Actualizar metadatos de una imagen
  updateImageMetadata: async (productId, imageId, metadata) => {
    try {
      const response = await apiClient.put(`/products/${productId}/images/${imageId}`, metadata);
      return formatApiResponse(response);
    } catch (error) {
      console.error('Error en updateImageMetadata:', error);
      return handleApiError(error);
    }
  },

  // Eliminar imagen de producto
  deleteProductImage: async (productId, imageId) => {
    try {
      const response = await apiClient.delete(`/products/${productId}/images/${imageId}`);
      return formatApiResponse(response);
    } catch (error) {
      console.error('Error en deleteProductImage:', error);
      return handleApiError(error);
    }
  },

  // Reordenar imágenes de producto
  reorderProductImages: async (productId, imageIds) => {
    try {
      const response = await apiClient.put(`/products/${productId}/images/reorder`, {
        image_ids: imageIds
      });
      return formatApiResponse(response);
    } catch (error) {
      console.error('Error en reorderProductImages:', error);
      return handleApiError(error);
    }
  }
};

// Servicio para ventas
export const ventasService = {
  // Obtener lista de ventas/documentos
  getVentas: async (params = {}) => {
    try {
      const response = await apiClient.get('/ventas', { params });
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  // Obtener documento por ID
  getDocumento: async (id) => {
    try {
      const response = await apiClient.get(`/ventas/${id}`);
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  // Crear nuevo documento (orden/estimado/factura)
  createDocumento: async (data) => {
    try {
      const response = await apiClient.post('/ventas', data);
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  // Actualizar documento
  updateDocumento: async (id, data) => {
    try {
      const response = await apiClient.put(`/ventas/${id}`, data);
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  // Eliminar documento
  deleteDocumento: async (id) => {
    try {
      const response = await apiClient.delete(`/ventas/${id}`);
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  // Convertir documento (estimado a orden, orden a factura)
  convertirDocumento: async (id, nuevoTipo) => {
    try {
      const response = await apiClient.post(`/ventas/${id}/convertir`, { tipo: nuevoTipo });
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  // Generar PDF del documento
  generarPDF: async (id) => {
    try {
      const response = await apiClient.get(`/ventas/${id}/pdf`, {
        responseType: 'blob'
      });
      return {
        success: true,
        data: response.data,
        headers: response.headers
      };
    } catch (error) {
      return handleApiError(error);
    }
  }
};

// El servicio de dashboard se importa desde dashboardService.js (ver línea 620)

// Exportar servicio de formulaciones
export const formulacionesService = formulacionesServiceModule;

// Exportar servicio de procesos de producción
export const procesosService = procesosServiceModule;

// Exportar servicio de inventario
export const inventarioService = inventarioServiceModule;

// Exportar servicio de dashboard
export const dashboardService = dashboardServiceModule;

// Servicio para proveedores
export const proveedoresService = {
  // Obtener lista de proveedores
  getProveedores: async (params = {}) => {
    try {
      const response = await apiClient.get('/suppliers', { params });
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  // Obtener proveedor por ID
  getProveedor: async (id) => {
    try {
      const response = await apiClient.get(`/suppliers/${id}`);
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  // Crear nuevo proveedor
  createProveedor: async (data) => {
    try {
      const response = await apiClient.post('/suppliers', data);
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  // Actualizar proveedor
  updateProveedor: async (id, data) => {
    try {
      const response = await apiClient.put(`/suppliers/${id}`, data);
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  // Eliminar proveedor
  deleteProveedor: async (id) => {
    try {
      const response = await apiClient.delete(`/suppliers/${id}`);
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  // Obtener estadísticas del proveedor
  getEstadisticas: async (id) => {
    try {
      const response = await apiClient.get(`/suppliers/${id}/stats`);
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  // Gestión de contactos
  createContacto: async (supplierId, data) => {
    try {
      const response = await apiClient.post(`/suppliers/${supplierId}/contacts`, data);
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  getContactos: async (supplierId) => {
    try {
      const response = await apiClient.get(`/suppliers/${supplierId}/contacts`);
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  updateContacto: async (contactId, data) => {
    try {
      const response = await apiClient.put(`/suppliers/contacts/${contactId}`, data);
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  deleteContacto: async (contactId) => {
    try {
      const response = await apiClient.delete(`/suppliers/contacts/${contactId}`);
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  // Obtener proveedor por ID (alias para compatibilidad)
  getProveedorById: async (id) => {
    try {
      const response = await apiClient.get(`/suppliers/${id}`);
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  // Obtener órdenes de compra del proveedor
  getOrdenesProveedor: async (id, params = {}) => {
    try {
      const response = await apiClient.get(`/suppliers/${id}/purchase-orders`, { params });
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  // ========================================================================
  // MÉTODOS PARA NOTAS DE PROVEEDORES
  // ========================================================================

  // Obtener notas de un proveedor
  getNotas: async (id, params = {}) => {
    try {
      const response = await apiClient.get(`/suppliers/${id}/notes`, { params });
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  // Crear nueva nota para un proveedor
  createNota: async (id, data) => {
    console.log("📤 POST /suppliers/" + id + "/notes con data:", data);
    try {
      const response = await apiClient.post(`/suppliers/${id}/notes`, data);
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  // Actualizar nota de un proveedor
  updateNota: async (supplierId, noteId, data) => {
    try {
      const response = await apiClient.put(`/suppliers/${supplierId}/notes/${noteId}`, data);
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  // Eliminar nota de un proveedor
  deleteNota: async (supplierId, noteId) => {
    try {
      const response = await apiClient.delete(`/suppliers/${supplierId}/notes/${noteId}`);
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }
};

// Servicio para vendedores/usuarios
export const vendedoresService = {
  // Obtener lista de vendedores
  getVendedores: async (params = {}) => {
    try {
      const response = await apiClient.get('/users', { params });
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  // Obtener vendedor por ID
  getVendedor: async (id) => {
    try {
      const response = await apiClient.get(`/users/${id}`);
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  // Crear nuevo vendedor
  createVendedor: async (data) => {
    try {
      const response = await apiClient.post('/users', data);
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  // Actualizar vendedor
  updateVendedor: async (id, data) => {
    try {
      const response = await apiClient.put(`/users/${id}`, data);
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  // Alternar estado activo/inactivo
  toggleEstado: async (id) => {
    try {
      const response = await apiClient.post(`/users/${id}/toggle-status`);
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  // Eliminar vendedor (soft delete)
  deleteVendedor: async (id) => {
    try {
      const response = await apiClient.delete(`/users/${id}`);
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  // Resetear contraseña
  resetPassword: async (id, newPassword) => {
    try {
      const response = await apiClient.post(`/users/${id}/reset-password`, {
        new_password: newPassword
      });
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  // Obtener vendedores por rol
  getVendedoresPorRol: async (rol) => {
    try {
      const response = await apiClient.get(`/users/by-role/${rol}`);
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  // Obtener estadísticas de usuarios
  getEstadisticas: async () => {
    try {
      const response = await apiClient.get('/users/stats');
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }
};

// Servicio para reportes
export const reportesService = {
  // ========== REPORTES GENERALES ==========

  // Obtener reporte de ventas (consulta invoices en backend)
  getReporteVentas: async (params = {}) => {
    try {
      const response = await apiClient.get('/reports/sales', { params });
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  // Alias para getReporteVentas (para compatibilidad con componentes)
  getVentas: async (params = {}) => {
    try {
      const response = await apiClient.get('/reports/sales', { params });
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  // Obtener productos más vendidos
  getTopProductos: async (params = {}) => {
    try {
      const response = await apiClient.get('/reports/top-products', { params });
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  // ========== REPORTES ESPECÍFICOS (FUNCIONALIDAD.PDF) ==========

  // Reporte de IMPUESTOS COBRADOS
  getImpuestosCobrados: async (params = {}) => {
    try {
      const response = await apiClient.get('/reports/tax-collected', { params });
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  // Reporte de INVENTARIO detallado
  getInventarioDetallado: async (params = {}) => {
    try {
      const response = await apiClient.get('/reports/inventory/detailed', { params });
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  // Reporte de RETORNOS
  getRetornos: async (params = {}) => {
    try {
      const response = await apiClient.get('/reports/returns', { params });
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  // Reporte de CRÉDITOS
  getCreditos: async (params = {}) => {
    try {
      const response = await apiClient.get('/reports/credits', { params });
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  // Reporte de VENTAS POR PRODUCTO específico
  getVentasPorProducto: async (productId, params = {}) => {
    try {
      const response = await apiClient.get(`/reports/sales/by-product/${productId}`, { params });
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  // Reporte de VENTAS POR CLIENTE específico
  getVentasPorCliente: async (clientId, params = {}) => {
    try {
      const response = await apiClient.get(`/reports/sales/by-client/${clientId}`, { params });
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  // ========== GENERACIÓN DE REPORTES (PDF/Excel) ==========

  // Generar reporte de ventas (PDF/Excel)
  generarReporteVentas: async (params) => {
    try {
      const response = await apiClient.post('/reportes/ventas', params, {
        responseType: 'blob'
      });
      return {
        success: true,
        data: response.data,
        headers: response.headers
      };
    } catch (error) {
      return handleApiError(error);
    }
  },

  // Generar reporte de inventario
  generarReporteInventario: async (params) => {
    try {
      const response = await apiClient.post('/reportes/inventario', params, {
        responseType: 'blob'
      });
      return {
        success: true,
        data: response.data,
        headers: response.headers
      };
    } catch (error) {
      return handleApiError(error);
    }
  },

  // Generar reporte de clientes
  generarReporteClientes: async (params) => {
    try {
      const response = await apiClient.post('/reportes/clientes', params, {
        responseType: 'blob'
      });
      return {
        success: true,
        data: response.data,
        headers: response.headers
      };
    } catch (error) {
      return handleApiError(error);
    }
  },

  // ========== EXPORTACIÓN (CSV/Excel/PDF) ==========

  // Exportar reporte de productos
  exportarProductos: async (params) => {
    try {
      const response = await apiClient.get('/reports/export/products', {
        params,
        responseType: 'blob'
      });
      return {
        success: true,
        data: response.data,
        headers: response.headers
      };
    } catch (error) {
      return handleApiError(error);
    }
  },

  // Exportar reporte de clientes
  exportarClientes: async (params) => {
    try {
      const response = await apiClient.get('/reports/export/clients', {
        params,
        responseType: 'blob'
      });
      return {
        success: true,
        data: response.data,
        headers: response.headers
      };
    } catch (error) {
      return handleApiError(error);
    }
  },

  // Exportar reporte de facturas
  exportarFacturas: async (params) => {
    try {
      const response = await apiClient.get('/reports/export/invoices', {
        params,
        responseType: 'blob'
      });
      return {
        success: true,
        data: response.data,
        headers: response.headers
      };
    } catch (error) {
      return handleApiError(error);
    }
  },

  // Exportar reporte de proveedores
  exportarProveedores: async (params) => {
    try {
      const response = await apiClient.get('/reports/export/suppliers', {
        params,
        responseType: 'blob'
      });
      return {
        success: true,
        data: response.data,
        headers: response.headers
      };
    } catch (error) {
      return handleApiError(error);
    }
  },

  // Exportar reporte de inventario
  exportarInventario: async (params) => {
    try {
      const response = await apiClient.get('/reports/export/inventory', {
        params,
        responseType: 'blob'
      });
      return {
        success: true,
        data: response.data,
        headers: response.headers
      };
    } catch (error) {
      return handleApiError(error);
    }
  },

  // Exportar reporte de formulaciones
  exportarFormulaciones: async (params) => {
    try {
      const response = await apiClient.get('/reports/export/formulas', {
        params,
        responseType: 'blob'
      });
      return {
        success: true,
        data: response.data,
        headers: response.headers
      };
    } catch (error) {
      return handleApiError(error);
    }
  }
};

// Servicio para categorías de clientes
export const clientCategoriesService = {
  // Obtener lista de categorías
  getCategorias: async (params = {}) => {
    try {
      const response = await apiClient.get('/client-categories', { params });
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  // Obtener categoría por ID
  getCategoriaById: async (id) => {
    try {
      const response = await apiClient.get(`/client-categories/${id}`);
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  // Crear nueva categoría
  crearCategoria: async (data) => {
    try {
      const response = await apiClient.post('/client-categories', data);
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  // Actualizar categoría
  actualizarCategoria: async (id, data) => {
    try {
      const response = await apiClient.put(`/client-categories/${id}`, data);
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  // Eliminar categoría (soft delete)
  eliminarCategoria: async (id) => {
    try {
      const response = await apiClient.delete(`/client-categories/${id}`);
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }
};

// Exportar servicio de facturas
export { facturasService, invoiceUtils } from './facturasService';

// Exportar servicio de prospectos (Seguimiento de Nuevos Clientes)
export { prospectService } from './prospectService';

// Servicios de administración
export { default as userService } from './userService';
export { default as rolesService } from './rolesService';
export { default as permissionsService } from './permissionsService';
export { default as adminSettingsService } from './adminSettingsService';

// Exportar apiClient para uso directo si es necesario
export { default as apiClient } from './apiClient';
